/**
 * Svelte use: action for dragging binder items between sidebar sections.
 * Desktop: mousedown starts drag. Mobile: long-press (600ms) then drag.
 * Clone is constrained to Y-axis only. Target section gets .drag-over class.
 *
 * Usage:
 *   <li use:sectiondrag={{
 *     onDrop: (binderData, targetSection) => { ... },
 *     binderData: { url, name, owner },
 *     sections: { my: element, recent: element },
 *   }}>
 *
 * @param {HTMLElement} node
 * @param {{ onDrop: Function, binderData: object, sections: { my: HTMLElement, recent: HTMLElement } }} options
 */
export function sectiondrag(node, options) {
  let { onDrop, binderData, sections } = options;

  const LONG_PRESS_MS = 600;
  const MOVE_THRESHOLD = 8;

  let clone = null;
  let dragging = false;
  let didDrag = false; // true if a drag was started (suppresses next click)
  let originX = 0;
  let startY = 0;
  let longPressTimer = null;
  let longPressFired = false;

  // Suppress the click event that fires after mouseup if we dragged
  function onClickCapture(e) {
    if (didDrag) {
      e.stopPropagation();
      e.preventDefault();
      didDrag = false;
    }
  }
  node.addEventListener('click', onClickCapture, true);

  // --- Desktop: mouse drag ---

  function onMouseDown(e) {
    // Only left click, ignore buttons/links
    if (e.button !== 0) return;
    if (e.target.closest('button, a')) return;

    startY = e.clientY;
    originX = node.getBoundingClientRect().left;
    didDrag = false;

    const onMouseMove = (me) => {
      if (!dragging) {
        if (Math.abs(me.clientY - startY) < MOVE_THRESHOLD) return;
        didDrag = true;
        startDrag(originX, me.clientY);
      }
      moveClone(me.clientY);
      hitTestSections(me.clientY);
    };

    const onMouseUp = () => {
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
      if (dragging) {
        endDrag();
      }
    };

    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
  }

  // --- Mobile: long-press then drag ---

  function onTouchStart(e) {
    if (e.touches.length !== 1) return;
    if (e.target.closest('button, a')) return;

    const touch = e.touches[0];
    startY = touch.clientY;
    originX = node.getBoundingClientRect().left;
    longPressFired = false;

    clearTimeout(longPressTimer);
    longPressTimer = setTimeout(() => {
      longPressFired = true;
      if (navigator.vibrate) navigator.vibrate(50);
      startDrag(originX, touch.clientY);
    }, LONG_PRESS_MS);
  }

  function onTouchMove(e) {
    if (!longPressFired && longPressTimer !== null) {
      // Cancel long press if moved too much before it fired
      const touch = e.touches[0];
      if (Math.abs(touch.clientY - startY) > 15 || Math.abs(touch.clientX - originX) > 15) {
        clearTimeout(longPressTimer);
        longPressTimer = null;
        return;
      }
    }

    if (!dragging) return;

    e.preventDefault();
    const touch = e.touches[0];
    moveClone(touch.clientY);
    hitTestSections(touch.clientY);
  }

  function onTouchEnd(e) {
    clearTimeout(longPressTimer);
    longPressTimer = null;

    if (dragging) {
      e.preventDefault();
      didDrag = true;
      endDrag();
    }
    longPressFired = false;
  }

  function onTouchCancel() {
    clearTimeout(longPressTimer);
    longPressTimer = null;
    cancelDrag();
    longPressFired = false;
  }

  // --- Drag lifecycle ---

  function startDrag(x, y) {
    dragging = true;
    node.classList.add('dragging');

    // Prevent text selection while dragging
    document.body.style.userSelect = 'none';
    document.body.style.webkitUserSelect = 'none';

    // Create clone
    clone = /** @type {HTMLElement} */ (node.cloneNode(true));
    const rect = node.getBoundingClientRect();
    Object.assign(clone.style, {
      position: 'fixed',
      left: rect.left + 'px',
      top: y - rect.height / 2 + 'px',
      width: rect.width + 'px',
      zIndex: '9999',
      pointerEvents: 'none',
      opacity: '0.9',
      boxShadow: '0 4px 16px rgba(0,0,0,0.2)',
      borderRadius: '8px',
      transition: 'none',
      listStyle: 'none',
    });
    document.body.appendChild(clone);
  }

  function moveClone(y) {
    if (!clone) return;
    const rect = node.getBoundingClientRect();
    clone.style.top = y - rect.height / 2 + 'px';
    // X stays fixed (Y-axis only constraint)
  }

  function hitTestSections(y) {
    if (!sections) return;

    for (const el of Object.values(sections)) {
      if (!el) continue;
      const rect = el.getBoundingClientRect();
      if (y >= rect.top && y <= rect.bottom) {
        el.classList.add('drag-over');
      } else {
        el.classList.remove('drag-over');
      }
    }
  }

  function getDropTarget(y) {
    if (!sections) return null;
    for (const [key, el] of Object.entries(sections)) {
      if (!el) continue;
      const rect = el.getBoundingClientRect();
      if (y >= rect.top && y <= rect.bottom) return key;
    }
    return null;
  }

  function endDrag() {
    if (!dragging) return;

    // Determine drop target from clone position
    let dropY = 0;
    if (clone) {
      const cloneRect = clone.getBoundingClientRect();
      dropY = cloneRect.top + cloneRect.height / 2;
    }

    const target = getDropTarget(dropY);
    cleanup();

    if (target && onDrop) {
      onDrop(binderData, target);
    }
  }

  function cancelDrag() {
    cleanup();
  }

  function cleanup() {
    dragging = false;
    node.classList.remove('dragging');
    document.body.style.userSelect = '';
    document.body.style.webkitUserSelect = '';
    if (clone) {
      clone.remove();
      clone = null;
    }
    // Remove drag-over from all sections
    if (sections) {
      for (const el of Object.values(sections)) {
        if (el) el.classList.remove('drag-over');
      }
    }
  }

  // --- Event listeners ---

  node.addEventListener('mousedown', onMouseDown);
  node.addEventListener('touchstart', onTouchStart, { passive: true });
  node.addEventListener('touchmove', onTouchMove, { passive: false });
  node.addEventListener('touchend', onTouchEnd);
  node.addEventListener('touchcancel', onTouchCancel);

  return {
    update(newOptions) {
      onDrop = newOptions.onDrop;
      binderData = newOptions.binderData;
      sections = newOptions.sections;
    },
    destroy() {
      clearTimeout(longPressTimer);
      cleanup();
      node.removeEventListener('click', onClickCapture, true);
      node.removeEventListener('mousedown', onMouseDown);
      node.removeEventListener('touchstart', onTouchStart);
      node.removeEventListener('touchmove', onTouchMove);
      node.removeEventListener('touchend', onTouchEnd);
      node.removeEventListener('touchcancel', onTouchCancel);
    },
  };
}
